import type { CrossPostPlatform } from './validators';

export const PLATFORM_MEDIA_SUPPORT: Record<
  CrossPostPlatform,
  { video: boolean; image: boolean; text: boolean }
> = {
  TIKTOK: { video: true, image: true, text: false },
  YOUTUBE: { video: true, image: false, text: false },
  INSTAGRAM: { video: true, image: true, text: false },
  FACEBOOK: { video: true, image: true, text: true },
  TWITTER: { video: true, image: true, text: true },
  LINKEDIN: { video: true, image: true, text: true },
  REDDIT: { video: false, image: false, text: false }
};

// The platforms a user can actually connect and cross-post to, in the order
// every surface lists them.
//
// One source of truth on purpose: this used to be hand-maintained in both the
// server's CONNECTABLE_PLATFORMS and the composer's own array, and the two
// drifted — the composer omitted FACEBOOK and listed REDDIT, so turning on auto
// cross-post for Facebook in settings did nothing, and Reddit sat there
// permanently "Not connected" (there is no Reddit OAuth, by decision).
export type ConnectablePlatform = Exclude<CrossPostPlatform, 'REDDIT'>;
export const CONNECTABLE_PLATFORMS: ConnectablePlatform[] = [
  'TIKTOK',
  'YOUTUBE',
  'INSTAGRAM',
  'FACEBOOK',
  'TWITTER',
  'LINKEDIN'
];

export const PLATFORM_LABELS: Record<CrossPostPlatform, string> = {
  TIKTOK: 'TikTok',
  YOUTUBE: 'YouTube',
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  TWITTER: 'X / Twitter',
  LINKEDIN: 'LinkedIn',
  REDDIT: 'Reddit'
};

export function getContentType(
  uploads: { type: string }[]
): 'video' | 'image' | 'text' {
  if (uploads.some((u) => u.type === 'VIDEO')) return 'video';
  if (uploads.some((u) => u.type === 'IMAGE')) return 'image';
  return 'text';
}

const CONTENT_NOUNS: Record<string, string> = {
  video: 'video',
  image: 'photos',
  text: 'text'
};

// ── Status verbiage ───────────────────────────────────────────────
// All user-facing cross-post status/failure wording lives in this file (these
// labels, unsupportedReason, and the validateForPlatform issue messages) so
// the composer popup, post-page platform cards, and server errors never drift.
export const CROSSPOST_STATUS_TEXT = {
  notConnected: 'Not connected'
} as const;

export function notConnectedError(platform: CrossPostPlatform): string {
  return `${PLATFORM_LABELS[platform]} is not connected`;
}

// unsupportedReason's compact variant for tight UI rows (composer popup).
export function shortUnsupportedReason(contentType: string): string {
  return contentType === 'text'
    ? 'No media attached'
    : `No ${contentType} support`;
}

// Why a platform can't take this content type, naming what it does accept.
export function unsupportedReason(
  platform: CrossPostPlatform,
  contentType: string
): string {
  const support = PLATFORM_MEDIA_SUPPORT[platform];
  const label = PLATFORM_LABELS[platform];
  const noun = CONTENT_NOUNS[contentType] ?? contentType;

  const accepts = [
    support.video && 'video',
    support.image && 'photos',
    support.text && 'text'
  ].filter((x): x is string => Boolean(x));

  if (accepts.length === 0) return `${label} cross-posting isn't available yet`;

  const list =
    accepts.length === 1
      ? accepts[0]
      : `${accepts.slice(0, -1).join(', ')} or ${accepts[accepts.length - 1]}`;

  return `${label} only supports ${list}, not ${noun}`;
}

// ── Platform content constraints ──────────────────────────────────
// Verified against official API docs (Aug 2026) for the exact endpoints the
// executor uses (IG = REELS container, LinkedIn = Videos API initializeUpload,
// Facebook = non-resumable file_url upload). Sizes in bytes. Durations in
// seconds. Every platform ingests progressive files only — none accept HLS;
// MP4 (H.264 + AAC, moov up front) is the one profile they all take.

interface MediaConstraints {
  maxSizeBytes: number;
  maxDurationSec?: number;
  minDurationSec?: number;
  maxCount?: number;
  allowedFormats: string[];
  maxTextLength?: number;
  /** width / height, e.g. 4:5 portrait = 0.8 */
  minAspectRatio?: number;
  maxAspectRatio?: number;
  /** Minimum length of the shorter side in px (portrait width / landscape height) */
  minShortSide?: number;
}

export interface PlatformConstraints {
  video?: MediaConstraints;
  image?: MediaConstraints;
  text?: { maxLength: number };
}

// POST /2/tweets takes up to 25,000 chars from an X Premium account (any
// tier). A free account is still capped at 280 weighted chars (URLs = 23,
// emoji/CJK = 2) and X rejects the post — we can't see the tier, so gate on
// the API ceiling and warn (platformTextWarning) past the free-account cap.
export const X_MAX_TEXT_LENGTH = 25000;
export const X_FREE_TEXT_LENGTH = 280;

export const X_PREMIUM_TEXT_WARNING = `Over ${X_FREE_TEXT_LENGTH} characters: only X Premium accounts can post this. Without Premium, X will reject it.`;
export const X_PREMIUM_TEXT_WARNING_SHORT = `Over ${X_FREE_TEXT_LENGTH} chars: needs X Premium`;

const MB = 1024 * 1024;
const GB = 1024 * MB;

export const PLATFORM_CONSTRAINTS: Record<
  CrossPostPlatform,
  PlatformConstraints
> = {
  TIKTOK: {
    video: {
      maxSizeBytes: 4 * GB,
      minDurationSec: 3,
      maxDurationSec: 600,
      maxCount: 1,
      // Content Posting API accepts MP4, WebM and MOV (H.264/H.265).
      allowedFormats: ['video/mp4', 'video/webm', 'video/quicktime'],
      // TikTok rejects sub-360p video async with picture_size_check_failed.
      minShortSide: 360
    },
    image: {
      maxSizeBytes: 20 * MB,
      maxCount: 35,
      allowedFormats: ['image/jpeg', 'image/webp']
    }
  },
  YOUTUBE: {
    video: {
      maxSizeBytes: 256 * GB,
      maxDurationSec: 43200,
      maxCount: 1,
      allowedFormats: [
        'video/mp4',
        'video/quicktime',
        'video/x-msvideo',
        'video/webm',
        'video/x-ms-wmv',
        'video/3gpp',
        'video/x-flv'
      ]
    }
  },
  INSTAGRAM: {
    // Videos publish as REELS containers: 3s–15min, up to 1GB, MOV/MP4 with
    // H.264/HEVC + AAC and the moov atom at the front of the file.
    video: {
      maxSizeBytes: 1 * GB,
      minDurationSec: 3,
      maxDurationSec: 900,
      maxCount: 1,
      allowedFormats: ['video/mp4', 'video/quicktime']
    },
    image: {
      maxSizeBytes: 8 * MB,
      maxCount: 10,
      allowedFormats: ['image/jpeg'],
      // Feed images must be between 4:5 portrait and 1.91:1 landscape or the
      // Graph API rejects the container (error 36003).
      minAspectRatio: 4 / 5,
      maxAspectRatio: 1.91
    },
    text: { maxLength: 2200 }
  },
  FACEBOOK: {
    // Non-resumable file_url upload caps at 1GB / 20min (resumable would
    // allow 10GB / 4h — switch these if the executor moves to it).
    video: {
      maxSizeBytes: 1 * GB,
      maxDurationSec: 1200,
      maxCount: 1,
      allowedFormats: ['video/mp4', 'video/quicktime']
    },
    image: {
      maxSizeBytes: 10 * MB,
      maxCount: 10,
      allowedFormats: [
        'image/jpeg',
        'image/png',
        'image/bmp',
        'image/gif',
        'image/tiff'
      ]
    },
    text: { maxLength: 63206 }
  },
  TWITTER: {
    video: {
      maxSizeBytes: 512 * MB,
      maxDurationSec: 140,
      maxCount: 1,
      allowedFormats: ['video/mp4', 'video/quicktime']
    },
    image: {
      maxSizeBytes: 5 * MB,
      maxCount: 4,
      allowedFormats: ['image/jpeg', 'image/png', 'image/gif']
    },
    text: { maxLength: X_MAX_TEXT_LENGTH }
  },
  LINKEDIN: {
    // Videos API (initializeUpload): MP4 only, 75KB–500MB. The 5GB figure
    // floating around is the member-facing uploader, not this API.
    video: {
      maxSizeBytes: 500 * MB,
      minDurationSec: 3,
      maxDurationSec: 1800,
      maxCount: 1,
      allowedFormats: ['video/mp4']
    },
    image: {
      maxSizeBytes: 36152320,
      maxCount: 20,
      allowedFormats: ['image/jpeg', 'image/png', 'image/gif']
    },
    text: { maxLength: 3000 }
  },
  REDDIT: {}
};

export interface ValidationIssue {
  platform: CrossPostPlatform;
  field: string;
  message: string;
}

// Metadata for the file that will actually be sent to the platform. Unknown
// fields (0 size, empty contentType, null duration/dimensions) skip their
// checks rather than failing them, so partial metadata never false-flags.
export interface UploadMeta {
  type: 'VIDEO' | 'IMAGE';
  sizeBytes: number;
  contentType: string;
  durationSec?: number | null;
  width?: number | null;
  height?: number | null;
}

// The executor sends images as the JPEG VIEW variant and videos as the RAW
// original (see apps/server crosspost/executor.ts runCrossPost) — validation
// must always describe those files, never the other variants.
export function outboundUploadMeta(
  uploads: {
    type: string;
    variants: {
      kind: string;
      size: number;
      contentType: string;
      width?: number | null;
      height?: number | null;
    }[];
  }[]
): UploadMeta[] {
  return uploads.map((u) => {
    const variant =
      u.type === 'IMAGE'
        ? (u.variants.find((v) => v.kind === 'VIEW') ?? u.variants[0])
        : (u.variants.find((v) => v.kind === 'RAW') ?? u.variants[0]);
    return {
      type: u.type as 'VIDEO' | 'IMAGE',
      sizeBytes: variant?.size ?? 0,
      contentType: variant?.contentType ?? '',
      durationSec: null,
      width: variant?.width ?? null,
      height: variant?.height ?? null
    };
  });
}

// UploadMeta for a local file that hasn't been uploaded yet (composer
// preview). Mirrors outboundUploadMeta: videos are sent as-is, but images are
// re-encoded to a JPEG VIEW variant server-side, so the local image's format
// and byte size don't predict the outbound file.
export function localUploadMeta(file: {
  isVideo?: boolean;
  sizeBytes?: number;
  mimeType?: string;
  durationSec?: number | null;
  width?: number | null;
  height?: number | null;
}): UploadMeta {
  if (file.isVideo) {
    return {
      type: 'VIDEO',
      sizeBytes: file.sizeBytes ?? 0,
      contentType: file.mimeType ?? '',
      durationSec: file.durationSec ?? null,
      width: file.width ?? null,
      height: file.height ?? null
    };
  }
  return {
    type: 'IMAGE',
    sizeBytes: 0,
    contentType: 'image/jpeg',
    durationSec: null,
    width: file.width ?? null,
    height: file.height ?? null
  };
}

function formatBytes(bytes: number): string {
  if (bytes >= GB) return `${(bytes / GB).toFixed(1)} GB`;
  return `${Math.round(bytes / MB)} MB`;
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

/** 3024×4032 → "3:4"; falls back to a decimal for irreducible dimensions */
function formatAspectRatio(width: number, height: number): string {
  const d = gcd(width, height);
  const w = width / d;
  const h = height / d;
  if (w <= 20 && h <= 20) return `${w}:${h}`;
  return `${(width / height).toFixed(2)}:1`;
}

/** 0.8 → "4:5", 1.91 → "1.91:1" */
function ratioLabel(ratio: number): string {
  for (let q = 1; q <= 10; q++) {
    const p = ratio * q;
    if (Math.abs(p - Math.round(p)) < 1e-9) return `${Math.round(p)}:${q}`;
  }
  return `${ratio.toFixed(2)}:1`;
}

export function validateForPlatform(
  platform: CrossPostPlatform,
  uploads: UploadMeta[],
  textLength: number
): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const constraints = PLATFORM_CONSTRAINTS[platform];
  if (!constraints) return issues;

  const videos = uploads.filter((u) => u.type === 'VIDEO');
  const images = uploads.filter((u) => u.type === 'IMAGE');

  if (videos.length > 0 && constraints.video) {
    const vc = constraints.video;

    if (vc.maxCount && videos.length > vc.maxCount) {
      issues.push({
        platform,
        field: 'video.count',
        message: `Max ${vc.maxCount} video${vc.maxCount > 1 ? 's' : ''}`
      });
    }

    for (const v of videos) {
      if (v.sizeBytes > vc.maxSizeBytes) {
        issues.push({
          platform,
          field: 'video.size',
          message: `Video too large (${formatBytes(v.sizeBytes)}, max ${formatBytes(vc.maxSizeBytes)})`
        });
      }
      if (
        vc.maxDurationSec &&
        v.durationSec &&
        v.durationSec > vc.maxDurationSec
      ) {
        issues.push({
          platform,
          field: 'video.duration',
          message: `Video too long (${Math.round(v.durationSec)}s, max ${vc.maxDurationSec}s)`
        });
      }
      if (
        vc.minDurationSec &&
        v.durationSec &&
        v.durationSec < vc.minDurationSec
      ) {
        issues.push({
          platform,
          field: 'video.duration',
          message: `Video too short (${Math.round(v.durationSec)}s, min ${vc.minDurationSec}s)`
        });
      }
      if (
        vc.allowedFormats.length > 0 &&
        v.contentType &&
        !vc.allowedFormats.includes(v.contentType)
      ) {
        issues.push({
          platform,
          field: 'video.format',
          message: `Unsupported format (${v.contentType})`
        });
      }
      if (vc.minShortSide && v.width && v.height) {
        const shortSide = Math.min(v.width, v.height);
        if (shortSide < vc.minShortSide) {
          issues.push({
            platform,
            field: 'video.resolution',
            message: `Video resolution too low (${v.width}×${v.height}, min ${vc.minShortSide}p)`
          });
        }
      }
    }
  }

  if (images.length > 0 && constraints.image) {
    const ic = constraints.image;

    if (ic.maxCount && images.length > ic.maxCount) {
      issues.push({
        platform,
        field: 'image.count',
        message: `Max ${ic.maxCount} image${ic.maxCount > 1 ? 's' : ''}`
      });
    }

    for (const img of images) {
      if (img.sizeBytes > ic.maxSizeBytes) {
        issues.push({
          platform,
          field: 'image.size',
          message: `Image too large (${formatBytes(img.sizeBytes)}, max ${formatBytes(ic.maxSizeBytes)})`
        });
      }
      if (
        ic.allowedFormats.length > 0 &&
        img.contentType &&
        !ic.allowedFormats.includes(img.contentType)
      ) {
        issues.push({
          platform,
          field: 'image.format',
          message: `Unsupported format (${img.contentType})`
        });
      }
      if (img.width && img.height) {
        const ratio = img.width / img.height;
        if (ic.minAspectRatio && ratio < ic.minAspectRatio) {
          issues.push({
            platform,
            field: 'image.aspectRatio',
            message: `Image too tall (${formatAspectRatio(img.width, img.height)}, min ${ratioLabel(ic.minAspectRatio)})`
          });
        }
        if (ic.maxAspectRatio && ratio > ic.maxAspectRatio) {
          issues.push({
            platform,
            field: 'image.aspectRatio',
            message: `Image too wide (${formatAspectRatio(img.width, img.height)}, max ${ratioLabel(ic.maxAspectRatio)})`
          });
        }
      }
    }
  }

  if (constraints.text && textLength > constraints.text.maxLength) {
    issues.push({
      platform,
      field: 'text.length',
      message: `Text too long (${textLength} chars, max ${constraints.text.maxLength})`
    });
  }

  return issues;
}

// A heads-up, not a block: the post may still go out (e.g. X Premium takes
// long text), but it fails on accounts we can't tell apart from here.
export function platformTextWarning(
  platform: CrossPostPlatform,
  textLength: number
): string | null {
  if (
    platform === 'TWITTER' &&
    textLength > X_FREE_TEXT_LENGTH &&
    textLength <= X_MAX_TEXT_LENGTH
  )
    return X_PREMIUM_TEXT_WARNING;
  return null;
}

export type PlatformContentCheck =
  | { ok: true }
  | { ok: false; reason: string; issues: ValidationIssue[] };

// The single verdict for "can this content go to this platform" — media-type
// support plus the per-platform constraints above. Every surface (composer
// preview, compatibility modal, server submit) must use this so they can
// never disagree.
export function checkPlatformContent(
  platform: CrossPostPlatform,
  uploads: UploadMeta[],
  textLength: number
): PlatformContentCheck {
  const contentType = getContentType(uploads);
  if (!PLATFORM_MEDIA_SUPPORT[platform][contentType]) {
    return {
      ok: false,
      reason: unsupportedReason(platform, contentType),
      issues: []
    };
  }
  const issues = validateForPlatform(platform, uploads, textLength);
  if (issues.length > 0) {
    return { ok: false, reason: issues[0].message, issues };
  }
  return { ok: true };
}
